const ACCESS_GRANTING_STATUSES = new Set(["active", "past_due"]);

/** Structural Timestamp shape (firebase Timestamp or a compatible stub). */
interface TimestampLike {
  toMillis?: () => number;
  toDate?: () => Date;
}

export interface SubscriptionAccessFields {
  status?: string | null;
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: TimestampLike | null;
}

function toMillis(value: TimestampLike): number | null {
  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }
  if (typeof value.toDate === "function") {
    return value.toDate().getTime();
  }
  return null;
}

/**
 * Whether a subscription document currently grants premium access.
 *
 * Defense in depth against stale backend state: a canceled-at-period-end
 * subscription whose paid period has lapsed no longer grants access even if
 * its status is still "active" (the backend sweep normally flips it to
 * "canceled" within a minute, but the UI must not lock the user out of
 * repurchasing in the meantime). The period check is scoped to
 * cancelAtPeriodEnd so past_due subscriptions mid-dunning are not demoted —
 * payment retries legitimately run past the period end.
 */
export function isSubscriptionGrantingAccess(
  subscription: SubscriptionAccessFields,
  now: number = Date.now()
): boolean {
  if (
    typeof subscription.status !== "string" ||
    !ACCESS_GRANTING_STATUSES.has(subscription.status)
  ) {
    return false;
  }
  if (subscription.cancelAtPeriodEnd === true && subscription.currentPeriodEnd) {
    const periodEndMillis = toMillis(subscription.currentPeriodEnd);
    if (periodEndMillis !== null && periodEndMillis < now) {
      return false;
    }
  }
  return true;
}
